/**
 * signup-bar custom element
 * Bell icon → modal drawer with phone number form → Klaviyo SMS subscription.
 *
 * Uses document-level event delegation for the trigger so it works regardless
 * of connectedCallback timing. The <dialog> is moved to <body> on first open
 * to escape any transform / overflow / stacking-context issues in the grid.
 */

// Delegate bell clicks at the document level — reliable even with deferred scripts
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-signup-open]');
  if (!trigger) return;

  const bar = trigger.closest('signup-bar');
  if (bar) bar.open();
});

class SignupBar extends HTMLElement {
  connectedCallback() {
    if (this._ready) return; // guard against re-connection
    this._ready = true;

    this._trigger = this.querySelector('[data-signup-open]');
    this._dialog  = this.querySelector('dialog');
    this._form    = this._dialog?.querySelector('[data-signup-form]');
    this._success = this._dialog?.querySelector('.signup-bar__success');
    this._error   = this._dialog?.querySelector('.signup-bar__error');

    if (!this._dialog) return;

    // Close button
    this._dialog.querySelectorAll('[data-signup-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Backdrop click closes
    this._dialog.addEventListener('click', (e) => {
      if (e.target === this._dialog) this.close();
    });

    // ESC key
    this._dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this._form?.addEventListener('submit', (e) => this._onSubmit(e));
  }

  open() {
    if (!this._dialog) {
      // fallback: find dialog if connectedCallback hasn't run yet
      this._dialog = this.querySelector('dialog');
      if (!this._dialog) return;
    }

    // Teleport to body once — escapes grid transforms / overflow:hidden
    if (this._dialog.parentNode !== document.body) {
      document.body.appendChild(this._dialog);
    }

    this._dialog.removeAttribute('inert');

    try {
      this._dialog.showModal();
    } catch (_) {
      this._dialog.setAttribute('open', '');
    }

    // Focus the phone input after the slide-in animation settles
    const input = this._dialog.querySelector('input');
    if (input) setTimeout(() => input.focus(), 50);

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
  }

  close() {
    if (!this._dialog) return;
    try { this._dialog.close(); } catch (_) { this._dialog.removeAttribute('open'); }
    this._dialog.setAttribute('inert', '');
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    this._trigger?.focus();
  }

  async _onSubmit(e) {
    e.preventDefault();

    const input     = this._form.querySelector('[name="phone"]');
    const phone     = input?.value.trim();
    const submitBtn = this._form.querySelector('.signup-bar__submit');

    if (!phone) return;

    submitBtn.disabled = true;
    if (this._error) this._error.hidden = true;

    try {
      await this._subscribeToKlaviyo(phone);
      this._form.hidden = true;
      if (this._success) this._success.hidden = false;
    } catch {
      if (this._error) this._error.hidden = false;
      submitBtn.disabled = false;
    }
  }

  async _subscribeToKlaviyo(phone) {
    const companyId = this.dataset.companyId;
    const listId    = this.dataset.listId;

    if (!companyId) throw new Error('Missing Klaviyo public API key in theme settings.');
    if (!listId) throw new Error('Missing Klaviyo list ID in theme settings (or product override metafield).');

    const consentedAt = new Date().toISOString();

    const digitsOnly = phone.replace(/[^\d+]/g, '');
    const normalizedPhone = digitsOnly.startsWith('+') ? digitsOnly : `+${digitsOnly}`;

    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2023-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'waitlist-bell',
              subscriptions: {
                sms: {
                  marketing: {
                    consent: 'SUBSCRIBED',
                    consented_at: consentedAt,
                  },
                },
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: { phone_number: normalizedPhone },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        }),
      }
    );

    if (!res.ok && res.status !== 202) {
      let errorMessage = `Klaviyo error: ${res.status}`;
      try {
        const body = await res.json();
        const apiMessage = body?.errors?.[0]?.detail || body?.errors?.[0]?.title;
        if (apiMessage) errorMessage = apiMessage;
      } catch (_) {
        // Ignore JSON parsing issues and keep fallback status-based message.
      }
      throw new Error(errorMessage);
    }
  }
}

// Inline script in snippets/signup-bar.liquid may register first; avoid duplicate define errors.
if (!customElements.get('signup-bar')) {
  customElements.define('signup-bar', SignupBar);
}
